import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Save, RotateCcw, IndianRupee, Sparkles, ToggleRight } from "lucide-react";

export default function AdminGigCommission() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gig-commission/config");
      setCfg(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const update = (path, value) => {
    const copy = { ...cfg };
    const segs = path.split(".");
    let cur = copy;
    for (let i = 0; i < segs.length - 1; i++) { cur[segs[i]] = { ...(cur[segs[i]] || {}) }; cur = cur[segs[i]]; }
    cur[segs[segs.length - 1]] = value;
    setCfg(copy);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/gig-commission/config", cfg);
      toast.success("Commission config saved");
      await fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!confirm("Reset to defaults?")) return;
    try {
      await api.post("/gig-commission/config/reset");
      toast.success("Reset complete");
      await fetch();
    } catch (err) { toast.error("Reset failed"); }
  };

  const runPreview = async () => {
    try {
      const { data } = await api.get("/gig-commission/preview?distance_km=5&leg=pickup&college=false");
      setPreview(data);
    } catch (err) { toast.error("Preview failed"); }
  };

  if (loading || !cfg) return <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-gig-commission">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-slate-900">Gig Agent Commission</h1>
          <p className="text-sm text-slate-500 mt-0.5">Per-pickup + per-delivery payouts (Playbook model)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} className="rounded-lg" data-testid="reset-btn"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset</Button>
          <Button onClick={runPreview} variant="outline" className="rounded-lg" data-testid="preview-btn"><Sparkles className="h-3.5 w-3.5 mr-1" /> Preview</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FF6B00] hover:bg-[#E65A00] rounded-lg" data-testid="save-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save
          </Button>
        </div>
      </div>

      {preview && (
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100 flex gap-4 text-sm" data-testid="preview-result">
          <IndianRupee className="h-5 w-5 text-emerald-600 mt-0.5" />
          <div>
            <p className="font-bold text-emerald-900">Preview (5km pickup): ₹{preview.total}</p>
            <p className="text-xs text-emerald-700">Base ₹{preview.base} + Distance ₹{preview.distance_bonus} + Surge ₹{preview.surge_bonus} + College ₹{preview.college_bonus}</p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <Section title="Base Fees">
          <Field label="Pickup Base (₹)" value={cfg.base_pickup_fee} onChange={(v) => update("base_pickup_fee", parseFloat(v))} testid="base-pickup-fee" />
          <Field label="Last-Mile Delivery (₹)" value={cfg.last_mile_delivery_fee} onChange={(v) => update("last_mile_delivery_fee", parseFloat(v))} testid="last-mile-fee" />
        </Section>

        <Section title="Bonuses">
          <Field label="Distance Threshold (km)" value={cfg.distance_bonus_threshold_km} onChange={(v) => update("distance_bonus_threshold_km", parseFloat(v))} testid="distance-threshold" />
          <Field label="Distance Bonus (₹)" value={cfg.distance_bonus_amount} onChange={(v) => update("distance_bonus_amount", parseFloat(v))} testid="distance-bonus" />
          <Field label="Surge Bonus (₹)" value={cfg.surge_bonus} onChange={(v) => update("surge_bonus", parseFloat(v))} testid="surge-bonus" />
          <Field label="College Bonus (₹)" value={cfg.college_bonus} onChange={(v) => update("college_bonus", parseFloat(v))} testid="college-bonus" />
        </Section>

        <Section title="Rewards">
          <Field label="Rating Bonus/month (₹)" value={cfg.rating_bonus_amount} onChange={(v) => update("rating_bonus_amount", parseFloat(v))} testid="rating-bonus" />
          <Field label="Refer-Agent Bonus (₹)" value={cfg.refer_agent_bonus} onChange={(v) => update("refer_agent_bonus", parseFloat(v))} testid="refer-bonus" />
          <Field label="Joining Bonus (₹)" value={cfg.joining_bonus} onChange={(v) => update("joining_bonus", parseFloat(v))} testid="joining-bonus" />
        </Section>

        <Section title="Security Deposit">
          <Field label="Amount (₹)" value={cfg.security_deposit_amount} onChange={(v) => update("security_deposit_amount", parseFloat(v))} testid="deposit-amount" />
          <Field label="Auto-Refund After (hours)" value={cfg.security_deposit_refund_hours} onChange={(v) => update("security_deposit_refund_hours", parseInt(v))} testid="deposit-refund-hours" />
        </Section>

        <Section title="Pickup Density Bonus">
          <p className="text-[11px] text-slate-500 -mt-2 mb-2">Reward agents for back-to-back pickups in hot zones. Flat bonus added per pickup once threshold is crossed in the rolling window. Capped daily.</p>
          <button
            type="button"
            onClick={() => update("pickup_density_bonus_enabled", !cfg.pickup_density_bonus_enabled)}
            className={`w-full p-3 rounded-xl border-2 text-left transition ${cfg.pickup_density_bonus_enabled ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}
            data-testid="density-bonus-toggle"
          >
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-0.5">Status</p>
            <p className={`text-sm font-bold ${cfg.pickup_density_bonus_enabled ? "text-emerald-700" : "text-slate-500"}`}>{cfg.pickup_density_bonus_enabled ? "ENABLED" : "DISABLED"}</p>
          </button>
          <Field label="Threshold (pickups)" value={cfg.pickup_density_threshold} onChange={(v) => update("pickup_density_threshold", parseInt(v))} testid="density-threshold" />
          <Field label="Window (minutes)" value={cfg.pickup_density_window_minutes} onChange={(v) => update("pickup_density_window_minutes", parseInt(v))} testid="density-window" />
          <Field label="Bonus per pickup (₹)" value={cfg.pickup_density_bonus_amount} onChange={(v) => update("pickup_density_bonus_amount", parseFloat(v))} testid="density-bonus-amount" />
          <Field label="Daily cap (₹)" value={cfg.pickup_density_max_bonus_per_day} onChange={(v) => update("pickup_density_max_bonus_per_day", parseFloat(v))} testid="density-daily-cap" />
        </Section>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="toggle-section">
        <div className="flex items-center gap-2 mb-3">
          <ToggleRight className="h-5 w-5 text-[#FF6B00]" />
          <h2 className="font-['Outfit'] font-bold text-slate-900">Use Gig Commission Per Order Type</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">When OFF, that order type keeps the legacy 80% flat commission.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {["courier", "b2b_express", "b2b_return"].map((k) => {
            const on = cfg.use_gig_commission_by_order_type?.[k];
            return (
              <button
                key={k}
                onClick={() => update(`use_gig_commission_by_order_type.${k}`, !on)}
                className={`p-4 rounded-xl border-2 text-left transition ${on ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}
                data-testid={`toggle-${k}`}
              >
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{k.replace("_", " ")}</p>
                <p className={`text-sm font-bold ${on ? "text-emerald-700" : "text-slate-500"}`}>{on ? "GIG (per-pickup)" : "LEGACY (80% flat)"}</p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5">
      <h3 className="font-['Outfit'] font-bold text-slate-900 mb-4">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-lg" data-testid={testid} />
    </div>
  );
}
